// ── Support-email assembly for the error pages ───────────────────────────────
//
// Pure module (no 'use client') so the error boundary, global-error, and tests
// all import the same builder — the use-client-boundary rule keeps shared
// constants out of client modules.
//
// Two consumers with different limits: the mailto: href must stay comfortably
// inside URL-length limits (~2000 chars in the strictest mail clients), so its
// body is trimmed; the Copy-report button carries the untrimmed version.

export const SUPPORT_EMAIL = 'help@sunboundhomes.com'
export const MAILTO_BODY_MAX = 1800

export interface SupportEmailInput {
  digest: string | null
  occurrenceId: string | null
  /** Pathname only — the builder never sees query strings. */
  page: string | null
  atIso: string
  plain: string | null
  technical: string | null
}

export interface SupportEmail {
  subject: string
  body: string
}

export function buildSupportEmail(input: SupportEmailInput): SupportEmail {
  const ref = input.digest ?? input.occurrenceId ?? 'no reference'
  const subject = `Sunbound RCM error report — ref ${ref}`
  const lines = [
    'What happened (plain English):',
    input.plain ?? 'No explanation was generated — details below.',
    '',
    'For the support team:',
    input.technical ?? 'See the reference and page below.',
    '',
    `Reference: ${input.digest ?? '—'}`,
    `Report id: ${input.occurrenceId ?? '—'}`,
    `Page: ${input.page ?? '—'}`,
    `Time: ${input.atIso}`,
  ]
  return { subject, body: lines.join('\n') }
}

/**
 * The 1-click mailto. Mail clients cap the whole URL, and percent-encoding
 * expands newlines/spaces ~3x — so the ceiling is measured against the
 * ENCODED body, not the source text (Codex fold). Trims with a marker so a
 * support reader knows text was cut; the Copy-report button has the rest.
 */
export function supportMailtoHref(input: SupportEmailInput): string {
  const { subject, body } = buildSupportEmail(input)
  let text = body
  let encoded = encodeURIComponent(text)
  let keep = body.length
  while (encoded.length > MAILTO_BODY_MAX && keep > 12) {
    keep = Math.floor(keep * 0.85)
    text = `${body.slice(0, keep)}\n[trimmed]`
    encoded = encodeURIComponent(text)
  }
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encoded}`
}
